import React, { useMemo } from "react";
import {
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  LinearProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { RichTextDisplay, QuestionRenderer, DialogueGroupExerciseRenderer } from "@/components/common";
import QuestionImage from "../../ExamSession/QuestionImage";
import SimplePartInstruction from "./SimplePartInstruction";

/**
 * RC Exercise Session Component
 * Handles Reading Comprehension exercises
 * 
 * Features:
 * - Manual navigation through questions
 * - Simplified part instructions (just Part number)
 * - Reusable for attendance tests
 */
const RCExerciseSession = ({
  parts,
  currentPartIndex,
  currentGroupIndex,
  showInstructions,
  answers,
  sectionInfo,
  dayLabel,
  onAnswerSelect,
  onNext,
  onPrevious,
  getTotalQuestions,
  getAnsweredQuestions,
  seenPartInstructions,
}) => {
  const currentPart = useMemo(() => parts?.[currentPartIndex] || null, [parts, currentPartIndex]);

  const questionGroup = useMemo(() => currentPart?.questionGroups || [], [currentPart]);

  const currentGroup = useMemo(() => questionGroup?.[currentGroupIndex] || null, [questionGroup, currentGroupIndex]);

  const currentGroupType = useMemo(() => {
    return currentGroup?.questions?.[0]?.type || null;
  }, [currentGroup]);

  // Show part instructions
  if (showInstructions && currentPart && !seenPartInstructions?.includes(currentPart.partNumber)) {
    return (
      <SimplePartInstruction
        currentPart={currentPart}
        partStyle={{ p: 0, boxShadow: "none" }}
        onNext={onNext}
        onPrevious={onPrevious}
        isFirstPart={currentPartIndex === 0 && currentGroupIndex === 0}
      />
    );
  }

  return (
    <Box>
      {/* Header with Progress */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
          color: "white",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {sectionInfo?.title || "Reading Exercise"}
            </Typography>
            {dayLabel && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {dayLabel}
              </Typography>
            )}
          </Box>
          <Chip
            label={`${getAnsweredQuestions()} / ${getTotalQuestions()} Answered`}
            sx={{
              bgcolor: "white",
              color: "primary.main",
              fontWeight: "bold",
            }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={(getAnsweredQuestions() / getTotalQuestions()) * 100}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.3)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "white",
              borderRadius: 4,
            },
          }}
        />
      </Paper>

      {/* Part and Question Info */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="medium">
          Part {currentPart?.partNumber} - Question {currentGroupIndex + 1} of{" "}
          {questionGroup.length}
        </Typography>
      </Box>

      {/* Main Content - Side by side layout for content and questions */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          width: "100%",
          alignItems: { xs: "center", md: "flex-start" },
        }}
      >
        {/* Left side - Image or Content */}
        {currentGroup?.imageUrl && (
          <QuestionImage imageUrl={currentGroup.imageUrl} />
        )}

        {currentGroup?.content && !currentGroup?.imageUrl && (
          <Box
            sx={{
              width: { xs: "100%", md: "55%" },
              position: { md: "sticky" },
              top: { md: "20px" },
              alignSelf: "flex-start",
              mb: { xs: 3, md: 0 },
            }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                maxHeight: "90vh",
                overflow: "auto",
              }}
            >
              <RichTextDisplay content={currentGroup.content} />
            </Paper>
          </Box>
        )}

        {/* Right side - Questions */}
        <Box
          sx={{
            width:
              currentGroup?.imageUrl || currentGroup?.content
                ? { xs: "100%", md: "45%" }
                : "100%",
          }}
        >
          {currentGroupType === 'dialogue-group' ? (
            <DialogueGroupExerciseRenderer
              group={currentGroup}
              answers={answers}
              onAnswerChange={onAnswerSelect}
              showResult={false}
              disabled={false}
            />
          ) : (
            currentGroup?.questions?.map((question, index) => (
              <QuestionRenderer
                key={question._id}
                question={question}
                answer={answers[question._id]}
                onAnswerChange={(answer) => onAnswerSelect(question._id, answer, question.type)}
                showResult={false}
                disabled={false}
                questionNumber={question.number || index + 1}
              />
            ))
          )}
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between" mt={4} gap={2}>
        <Button
          variant="outlined"
          onClick={onPrevious}
          disabled={currentPartIndex === 0 && currentGroupIndex === 0}
          size="large"
          startIcon={<ArrowBackIcon />}
        >
          Previous
        </Button>
        {/* <Button
          variant="outlined"
          onClick={onExit}
          size="large"
        >
          Exit
        </Button> */}
        <Button
          variant="contained"
          onClick={onNext}
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          {currentPartIndex === parts.length - 1 &&
          currentGroupIndex === questionGroup.length - 1
            ? "Finish"
            : "Next"}
        </Button>
      </Box>
    </Box>
  );
};

export default RCExerciseSession;
